import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import moment from "moment/moment";

import {
  addUser,
  addPeople,
  updateUser,
  updatePeople,
} from "../../services/backend/UserService";
import {
  setUser,
  addPeople as addPeopleToStore,
  addUser as addUserToStore,
  updatePeople as updatePeopleToStore,
  updateUsers as updateUserToStore,
} from "../../redux/slices/userSlice";
import UploadFile from "./UploadFile";
import ProgressUploadFile from "./ProgressUploadFile";
import UserPasswordModal from "./UserPasswordModal";
import ChangeEmailDialog from "../myentity/ChangeEmailDialog";
import SignatureUpload from "./SignatureUpload";
import { debugLog } from "../../functions";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../config/ConfigFirebase";

const titles = [
  "Dr.", "Señor", "Señora", "Señores", "Estudiante", "Estudiantes", "Profesor",
  "Profesores", "Doctor", "Doctora", "Doctores"
];

// Role display labels (readable names)
const ROLE_LABELS = {
  ADMIN: "Administrador",
  ARCHIVER: "Archivador",
  EXPEDIENTS: "Expedientes",
  USER: "Usuario",
  ADMIN_CONTRACT: "Admin Contratos",
  SUPERVISOR_CONTRACT: "Supervisor Contratos",
  CONTRACTOR: "Contratista",
  EXTENSION_APPLICANTS: "Extensión — Gestor de Aspirantes",
  PAYMENT_CAUSATION: "Gestión de Pagos — Causación",
  PAYMENT_ACCOUNTING: "Gestión de Pagos — Contabilidad",
  PAYMENT_BUDGET: "Gestión de Pagos — Presupuesto",
  PAYMENT_TREASURY: "Gestión de Pagos — Tesorería",
  PAYMENT_CASHIER: "Gestión de Pagos — Caja",
  ENTITY_ADMIN: "Administrador de la empresa",
  ENTITY_MEMBER: "Miembro de la empresa",
};

// ─── Header Section ───────────────────────────────────────────────
const ProfileHeader = ({ user, file, handleFileUpload, onUploadFileUrl }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        background: "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          gap: { xs: 2, sm: 3 },
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <UploadFile accept={"image/*"} handleFileUpload={handleFileUpload} />
          }
        >
          <Avatar
            alt={`${user?.name || ""} ${user?.lastName || ""}`}
            src={user?.avatarUrl}
            sx={{
              width: { xs: 72, sm: 88 },
              height: { xs: 72, sm: 88 },
              border: "3px solid",
              borderColor: "primary.light",
              fontSize: "2rem",
              bgcolor: "primary.main",
            }}
          >
            {!user?.avatarUrl && (
              <PersonOutlineIcon sx={{ fontSize: "2.2rem" }} />
            )}
          </Avatar>
        </Badge>
        <ProgressUploadFile
          file={file}
          storagePath={user?.userId ? `/files/${user.userId}/profile.png` : null}
          onUploadFileUrl={onUploadFileUrl}
        />
        <Box
          sx={{
            textAlign: { xs: "center", sm: "left" },
            flex: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              lineHeight: 1.3,
            }}
          >
            {user?.name || ""} {user?.lastName || ""}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 0.5,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {user?.email || ""}
          </Typography>
          {user?.position && (
            <Chip
              label={user.position}
              size="small"
              variant="outlined"
              sx={{
                mt: 1,
                fontSize: "0.75rem",
                height: 24,
                borderColor: "grey.300",
                color: "text.secondary",
              }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── User Info Card ───────────────────────────────────────────────
const UserInfoCard = ({
  usertitle, setUsertitle,
  name, lastName,
  email, nuip, setNuip,
  address, setAddress,
  phone, setPhone,
  country, setCountry,
  city, setCity,
  postal, setPostal,
  entity, setEntity,
  dependence, setDependence,
  level, setLevel,
  position, setPosition,
  area, isSupervisor,
  birthDate, setBirthDate,
  birthDateError,
  loading, hasChanges,
  // Una cuenta de empresa. Cambia lo que se pide y cómo se cambia el acceso:
  // ver el bloque institucional y el botón de abajo.
  isEntity, companyName,
  onSubmit, onShowPasswordModal, onChangeEmail,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          color: "text.primary",
        }}
      >
        Información del usuario
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 3, fontSize: "0.8rem" }}
      >
        Actualiza tu información personal
      </Typography>

      <Box component="form" onSubmit={onSubmit}>
        <Grid container spacing={2.5}>
          {/* Title */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <TextField
                select
                label="Título"
                value={usertitle}
                onChange={(evt) => setUsertitle(evt.target.value)}
                size="small"
              >
                {titles.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>

          {/* Name */}
          <Grid item xs={12} sm={4.5}>
            <TextField
              label="Nombres"
              required
              fullWidth
              value={name}
              disabled
              size="small"
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} sm={4.5}>
            <TextField
              label="Apellidos"
              required
              fullWidth
              value={lastName}
              disabled
              size="small"
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Correo electrónico"
              required
              fullWidth
              value={email}
              disabled
              size="small"
            />
          </Grid>

          {/* NUIP */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Número de identificación"
              required
              fullWidth
              value={nuip}
              onChange={(evt) => setNuip(evt.target.value)}
              size="small"
            />
          </Grid>

          {/* Birth Date */}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Fecha de nacimiento"
              value={birthDate ? moment(birthDate) : null}
              onChange={(val) => {
                if (val && val.isValid()) {
                  setBirthDate(val.format("YYYY-MM-DD"));
                } else {
                  setBirthDate("");
                }
              }}
              maxDate={moment()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  // Obligatoria sólo para el personal interno, que es de quien
                  // la institución lleva hoja de vida. Exigírsela a una cuenta
                  // de empresa dejaba el botón «Guardar» apagado para siempre.
                  required: !isEntity,
                  error: !!birthDateError,
                  helperText: birthDateError || "",
                },
              }}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Celular"
              required
              fullWidth
              value={phone}
              onChange={(evt) => setPhone(evt.target.value)}
              size="small"
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Dirección"
              required
              fullWidth
              value={address}
              onChange={(evt) => setAddress(evt.target.value)}
              size="small"
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="País"
              fullWidth
              value={country}
              onChange={(evt) => setCountry(evt.target.value)}
              size="small"
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ciudad"
              required
              fullWidth
              value={city}
              onChange={(evt) => setCity(evt.target.value)}
              size="small"
            />
          </Grid>

          {/* Postal */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código postal"
              fullWidth
              value={postal}
              onChange={(evt) => setPostal(evt.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Entidad / dependencia / nivel describen el lugar de un funcionario
              DENTRO de la institución. Los datos institucionales de una cuenta
              de empresa son los de la EMPRESA y viven en `entities`, no en su
              ficha personal: pedírselos aquí la dejaba llenando casillas que no
              son suyas. Sólo el cargo aplica a los dos. */}
          {isEntity ? (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Empresa"
                fullWidth
                value={companyName || ""}
                disabled
                size="small"
                helperText="Los datos de tu empresa se editan en Mi Entidad."
              />
            </Grid>
          ) : (
            <>
              {/* Entity */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Entidad"
                  fullWidth
                  value={entity}
                  onChange={(evt) => setEntity(evt.target.value)}
                  size="small"
                />
              </Grid>

              {/* Area (supervisor only, read-only) */}
              {isSupervisor && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Área"
                    fullWidth
                    value={area || ""}
                    disabled
                    size="small"
                  />
                </Grid>
              )}

              {/* Dependence */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Dependencia"
                  fullWidth
                  value={dependence}
                  onChange={(evt) => setDependence(evt.target.value)}
                  size="small"
                />
              </Grid>

              {/* Level */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nivel"
                  fullWidth
                  value={level}
                  onChange={(evt) => setLevel(evt.target.value)}
                  size="small"
                />
              </Grid>
            </>
          )}

          {/* Position */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Cargo"
              fullWidth
              value={position}
              onChange={(evt) => setPosition(evt.target.value)}
              size="small"
            />
          </Grid>

          
        </Grid>

        {/* Actions */}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !hasChanges || !!birthDateError}
            >
              {loading ? <CircularProgress size={24} /> : <span>Guardar</span>}
            </Button>
            {/* Una cuenta de empresa NO tiene contraseña: se crea con
                createUser({ email }) a propósito y entra con un código, así que
                «Cambiar contraseña» no podía funcionar nunca (reauthenticate
                pide una credencial que no existe). Lo que sí necesita cambiar
                es el correo, que es su credencial. */}
            {isEntity ? (
              <Button variant="outlined" sx={{ ml: 1 }} onClick={onChangeEmail}>
                Cambiar mi correo
              </Button>
            ) : (
              <Button
                variant="outlined"
                sx={{ ml: 1 }}
                onClick={onShowPasswordModal}
              >
                Cambiar contraseña
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

// ─── Roles & Access Card ──────────────────────────────────────────
const RolesAccessCard = ({ roles }) => {
  if (!roles || roles.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          color: "text.primary",
        }}
      >
        Roles y acceso
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 2.5, fontSize: "0.8rem" }}
      >
        Permisos asignados a tu cuenta
      </Typography>

      <TableContainer
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.100",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "grey.50",
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                Rol
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                Tipo
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role, idx) => (
              <TableRow
                key={role}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "grey.50" },
                  transition: "background-color 0.15s",
                }}
              >
                <TableCell
                  sx={{
                    py: 1.8,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  {ROLE_LABELS[role] || role}
                </TableCell>
                <TableCell sx={{ py: 1.8 }}>
                  <Chip
                    label={role}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      height: 22,
                      bgcolor:
                        role === "ADMIN"
                          ? "error.50"
                          : role.includes("CONTRACT")
                          ? "warning.50"
                          : "primary.50",
                      color:
                        role === "ADMIN"
                          ? "error.dark"
                          : role.includes("CONTRACT")
                          ? "warning.dark"
                          : "primary.dark",
                      border: "1px solid",
                      borderColor:
                        role === "ADMIN"
                          ? "error.200"
                          : role.includes("CONTRACT")
                          ? "warning.200"
                          : "primary.200",
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const UserProfile = ({ userType, showPhoto, onSubmit: onSubmitProp }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  // Una cuenta de empresa. Su ficha personal pide menos y su acceso se cambia
  // de otra manera; el resto de la pantalla (foto, firma, roles) es igual.
  const isEntity = user?.userType === "ENTITY";
  // Form state
  const [usertitle, setUsertitle] = useState("Señor");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [postal, setPostal] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [nuip, setNuip] = useState("");
  const [entity, setEntity] = useState("");
  const [dependence, setDependence] = useState("");
  const [level, setLevel] = useState("");
  const [position, setPosition] = useState("");
  const [birthDate, setBirthDate] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [showUserPasswordModal, setShowUserPasswordModal] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, message: "", severity: "success" });
  const [hasSignature, setHasSignature] = useState(true); // assume true until checked

  // Check if signature exists in Storage
  useEffect(() => {
    if (!user?.userId) return;
    const storageRef = ref(storage, `/files/${user.userId}/signature.png`);
    getDownloadURL(storageRef)
      .then(() => setHasSignature(true))
      .catch(() => setHasSignature(false));
  }, [user?.userId]);

  // Track initial values for change detection
  const [initialValues, setInitialValues] = useState(null);

  // Populate form from user data
  useEffect(() => {
    if (user) {
      const vals = {
        usertitle: user.usertitle || "Señor",
        name: user.name || "",
        lastName: user.lastName || "",
        city: user.city || "",
        country: user.country || "",
        postal: user.postal || "",
        phone: user.phone || "",
        address: user.address || "",
        email: user.email || "",
        nuip: user.nuip || "",
        entity: user.entity || "",
        dependence: user.dependence || "",
        level: user.level || "",
        position: user.position || "",
        birthDate: user.birthDate || "",
      };
      setUsertitle(vals.usertitle);
      setName(vals.name);
      setLastName(vals.lastName);
      setCity(vals.city);
      setCountry(vals.country);
      setPostal(vals.postal);
      setPhone(vals.phone);
      setAddress(vals.address);
      setEmail(vals.email);
      setNuip(vals.nuip);
      setEntity(vals.entity);
      setDependence(vals.dependence);
      setLevel(vals.level);
      setPosition(vals.position);
      setBirthDate(vals.birthDate);
      setInitialValues(vals);
    }
  }, [user]);

  // Compute if there are changes
  const hasChanges = useMemo(() => {
    if (!initialValues) return false;
    return (
      usertitle !== initialValues.usertitle ||
      nuip !== initialValues.nuip ||
      address !== initialValues.address ||
      phone !== initialValues.phone ||
      country !== initialValues.country ||
      city !== initialValues.city ||
      postal !== initialValues.postal ||
      entity !== initialValues.entity ||
      dependence !== initialValues.dependence ||
      level !== initialValues.level ||
      position !== initialValues.position ||
      birthDate !== initialValues.birthDate
    );
  }, [
    initialValues, usertitle, nuip, address, phone,
    country, city, postal, entity, dependence, level, position, birthDate,
  ]);

  // Compute missing required fields for the profile completion alert
  //
  // Los dos conjuntos son los mismos que impone ProtectedRoute: si este aviso
  // pidiera algo distinto, alguien quedaría atascado llenando un campo que no
  // desbloquea nada, o pasaría el aviso y seguiría rebotando a /profile.
  const missingFields = useMemo(() => {
    if (!user) return [];
    const staffFields = {
      usertitle: "Título",
      name: "Nombres",
      lastName: "Apellidos",
      city: "Ciudad",
      phone: "Celular",
      address: "Dirección",
      email: "Correo electrónico",
      nuip: "Número de identificación",
      entity: "Entidad",
      dependence: "Dependencia",
      level: "Nivel",
      position: "Cargo",
    };
    const entityFields = {
      name: "Nombres",
      lastName: "Apellidos",
      nuip: "Número de identificación",
      phone: "Celular",
    };
    const fields = isEntity ? entityFields : staffFields;
    const missing = Object.entries(fields)
      .filter(([key]) => !user[key])
      .map(([, label]) => label);
    if (!hasSignature) missing.push("Firma electrónica");
    return missing;
  }, [user, hasSignature, isEntity]);

  // Validate birthDate (mandatory)
  const birthDateError = useMemo(() => {
    // Optional for a company account: nobody keeps a personnel file on them.
    if (!birthDate) return isEntity ? "" : "Este campo es obligatorio";
    const d = moment(birthDate, "YYYY-MM-DD", true);
    if (!d.isValid()) return "Fecha inválida";
    if (d.isAfter(moment())) return "La fecha no puede ser futura";
    return "";
  }, [birthDate, isEntity]);

  const handleFileUpload = useCallback((files) => {
    debugLog("files: ", files);
    setFile(files[0]);
  }, []);

  const onUploadFileUrl = useCallback(
    async (url) => {
    debugLog("url: ", url);
    try {
      const userData = { userId: user.userId, avatarUrl: url };
      await updateUser(userData, user);
      dispatch(setUser({ ...user, ...userData }));
    } catch (error) {
      debugLog("error: ", error);
    }
    },
    [user, dispatch]
  );

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (birthDateError) return;

    setLoading(true);

    try {
      const userData = {
        usertitle,
        name,
        lastName,
        city,
        country,
        postal,
        phone,
        address,
        nuip,
        position,
        birthDate: birthDate || null,
        // El correo de una cuenta de empresa es su credencial y handleUser
        // rechaza el UPDATE_USER entero si viene en el cuerpo (ver
        // authorizeSelfUpdate): se cambia por «Cambiar mi correo», que también
        // toca Firebase Auth. Los tres campos institucionales tampoco viajan:
        // no se le piden y mandarlos vacíos borraría lo que hubiera escrito un
        // gestor.
        ...(isEntity ? {} : { email, entity, dependence, level }),
      };

      if (userType === "users") {
        userData.userId = user?.userId;
        if (user) {
          // The email is view only on an existing account: it is also the
          // Firebase Auth sign-in address, and handleUser rejects any
          // UPDATE_USER that carries it. An ADMIN changes it from the user
          // manager (CHANGE_EMAIL).
          delete userData.email;
          await updateUser(userData, user);
          dispatch(updateUserToStore(userData));
        } else {
          await addUser(userData, user);
          dispatch(addUserToStore(userData));
        }
      } else if (userType === "people") {
        userData.peopleId = user?.peopleId;
        if (user) {
          await updatePeople(userData, user);
          dispatch(updatePeopleToStore(userData));
        } else {
          await addPeople(userData, user);
          dispatch(addPeopleToStore(userData));
        }
      }

      // Update initial values so hasChanges goes false
      setInitialValues({
        usertitle,
        name,
        lastName,
        city,
        country,
        postal,
        phone,
        address,
        email,
        nuip,
        entity,
        dependence,
        level,
        position,
        birthDate: birthDate || "",
      });

      // Also update the user in Redux to reflect birthDate
      dispatch(setUser({ ...user, ...userData }));

      if (onSubmitProp) onSubmitProp(userData);
      setSnackInfo({ open: true, message: "Perfil actualizado correctamente", severity: "success" });
    } catch (error) {
      debugLog("Error saving profile: ", error);
      setSnackInfo({ open: true, message: "Error al guardar los cambios", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <UserPasswordModal
        open={showUserPasswordModal}
        onClose={() => setShowUserPasswordModal(false)}
      />

      <ChangeEmailDialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
      />

      {/* Missing fields alert */}
      {missingFields.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Debes completar tu perfil para continuar
          </Typography>
          <Typography variant="body2">
            Campos pendientes: {missingFields.join(", ")}
          </Typography>
        </Alert>
      )}

      {/* Header */}
      {showPhoto && (
        <ProfileHeader
          user={user}
          file={file}
          handleFileUpload={handleFileUpload}
          onUploadFileUrl={onUploadFileUrl}
        />
      )}

      {/* User Info Card */}
      <UserInfoCard
        usertitle={usertitle} setUsertitle={setUsertitle}
        name={name} lastName={lastName}
        email={email}
        nuip={nuip} setNuip={setNuip}
        address={address} setAddress={setAddress}
        phone={phone} setPhone={setPhone}
        country={country} setCountry={setCountry}
        city={city} setCity={setCity}
        postal={postal} setPostal={setPostal}
        entity={entity} setEntity={setEntity}
        dependence={dependence} setDependence={setDependence}
        level={level} setLevel={setLevel}
        position={position} setPosition={setPosition}
        area={user?.area || ""}
        isSupervisor={user?.roles?.includes("SUPERVISOR_CONTRACT")}
        birthDate={birthDate} setBirthDate={setBirthDate}
        birthDateError={birthDateError}
        loading={loading}
        hasChanges={hasChanges}
        isEntity={isEntity}
        companyName={user?.entityName || ""}
        onSubmit={handleSubmit}
        onShowPasswordModal={() => setShowUserPasswordModal(true)}
        onChangeEmail={() => setShowEmailDialog(true)}
      />

      {/* Signature Upload */}
      <SignatureUpload
        userName={`${user?.name || ""} ${user?.lastName || ""}`.trim()}
        userId={user?.userId}
        onConfirm={() => {
          setHasSignature(true);
          setSnackInfo({ open: true, message: "Firma guardada correctamente", severity: "success" });
        }}
      />

      {/* Roles & Access Card */}
      <RolesAccessCard roles={user?.roles} />

      {/* Snackbar */}
      <Snackbar
        open={snackInfo.open}
        autoHideDuration={3500}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackInfo.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserProfile;
